import { ApiError } from 'EstraSponsor'

import {
  GeneralFieldsFormData,
  HolderSectionFormData,
  PrimaryBankDetailsFormData,
  SecondaryBankDetailsFormData,
  TechnicalDirectorFormData,
  TechnicalManagerFormData,
} from './formTypes'

export type SponsorUpdateApiError = {
  sponsorman?: Partial<
    {
      [key in keyof GeneralFieldsFormData]: string | string[]
    }
  >
  holder_address?: Partial<
    {
      [key in keyof HolderSectionFormData]: string | string[]
    }
  >
  technical_director_address?: Partial<
    {
      [key in keyof TechnicalDirectorFormData]: string | string[]
    }
  >
  technical_manager_address?: Partial<
    {
      [key in keyof TechnicalManagerFormData]: string | string[]
    }
  >
  bank_account_1?: Partial<
    {
      [key in keyof PrimaryBankDetailsFormData]: string | string[]
    }
  >
  bank_account_2?: Partial<
    {
      [key in keyof SecondaryBankDetailsFormData]: string | string[]
    }
  >
}

export type SponsorUpdateUserDataApiError = {
  sponsorman?: Partial<
    {
      [key in keyof Omit<
        GeneralFieldsFormData,
        | 'company_dm_letters'
        | 'standard_iso_9001'
        | 'standard_iso_14001'
        | 'standard_ohsas18001'
        | 'standard_iso_45001'
        | 'standard_iso_iec_27001'
        | 'standard_sa8000'
        | 'standard_other'
        | 'privacy_required'
        | 'privacy_optional_internal_marketing'
        | 'privacy_optional_external_marketing'
      >]: string | string[]
    }
  >
}

// Check if the error returned from the Api is an expected error with a validation inside
// or another generic Api Error
// TS TypeGuards
export function isSponsorUpdateApiError(
  error: ApiError
): error is SponsorUpdateApiError {
  const supplierUpdateError = error as SponsorUpdateApiError
  return (
    supplierUpdateError.sponsorman !== undefined ||
    supplierUpdateError.holder_address !== undefined ||
    supplierUpdateError.technical_director_address !== undefined ||
    supplierUpdateError.bank_account_1 !== undefined ||
    supplierUpdateError.bank_account_2 !== undefined
  )
}

// Check if there is at least one error in the first step
export function isSponsorUpdateUserDataApiError(
  error: SponsorUpdateApiError
): error is SponsorUpdateUserDataApiError {
  // Check if the sponsorman object is present, and if at least one field error of the first step is present,
  // excluding the general fields belonging to the second step
  return (
    error?.sponsorman !== undefined &&
    Object.keys(error.sponsorman).filter(
      (k) =>
        k !== 'company_dm_letters' &&
        k !== 'standard_iso_9001' &&
        k !== 'standard_iso_14001' &&
        k !== 'standard_ohsas18001' &&
        k !== 'standard_iso_45001' &&
        k !== 'standard_iso_iec_27001' &&
        k !== 'standard_sa8000' &&
        k !== 'standard_other' &&
        k !== 'privacy_required' &&
        k !== 'privacy_optional_internal_marketing' &&
        k !== 'privacy_optional_external_marketing'
    ).length !== 0
  )
}

export function getUserDataErrorsFromApiError(
  error: SponsorUpdateApiError
): SponsorUpdateUserDataApiError {
  if (!error.sponsorman)
    return {
      sponsorman: error.sponsorman,
    }

  // Extract from the error only the properties of the user data steps
  const {
    company_dm_letters,
    standard_iso_9001,
    standard_iso_14001,
    standard_ohsas18001,
    standard_iso_45001,
    standard_iso_iec_27001,
    standard_sa8000,
    standard_other,
    privacy_required,
    privacy_optional_internal_marketing,
    privacy_optional_external_marketing,
    ...rest
  } = error.sponsorman

  return {
    sponsorman: {
      ...rest,
    },
  }
}
